import { cloneElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Icon, Menu, MenuItem, Select, Stack, styled, Text, Tooltip } from 'shell/components';

const SubmissionNavigationDropdown = ({ disabled, currentSubmission, options, sort, setSort, selectSubmission }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { t } = useTranslation();

    const id = 'grading-select-submission-dropdown';
    const open = Boolean(anchorEl);

    const handleClick = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleClose = (e, { key, value }) => {
        e.stopPropagation();
        setAnchorEl(null);
        if (key && value) selectSubmission(key);
    };

    const RootComponent = (
        <Button
            sx={{ mt: -1 }}
            color={'neutral'}
            disabled={disabled}
            endIcon={<Icon kind={'dropdownArrowDown'} />}
            variant={'borderless'}
        >
            <Stack direction={'row'} spacing={2}>
                <Text weight={'semibold'}>{t('ui.progress', 'Progress')}</Text>
                {options.findIndex(({ key }) => key === currentSubmission.id) + 1}/{options.length}
            </Stack>
        </Button>
    );

    return (
        <Box>
            {cloneElement(RootComponent, {
                id,
                'aria-controls': open ? `${id}-menu` : undefined,
                'aria-expanded': open ? true : undefined,
                'aria-haspopup': true,
                onClick: handleClick,
            })}
            <CustomMenu
                id={`${id}-menu`}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                MenuListProps={{
                    'aria-labelledby': id,
                }}
                open={open}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                onClose={handleClose}
            >
                <Stack sx={{ px: 1 }} alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text weight={'medium'}>Submissions</Text>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text sx={{ flexShrink: 0 }} variant={'p3'} weight={'medium'}>
                            {t('ui.sort', 'Sort')}
                        </Text>
                        <Select size={'small'} value={sort} onChange={(e) => setSort(e.target.value)}>
                            <MenuItem value={'default'}>{t('ui.default', 'Default')}</MenuItem>
                            <MenuItem value={'title-asc'}>
                                {t('controls.sort-options.title-asc', 'Title (A-Z)')}
                            </MenuItem>
                            <MenuItem value={'title-desc'}>
                                {t('controls.sort-options.title-desc', 'Title (Z-A)')}
                            </MenuItem>
                            <MenuItem value={'grade-asc'}>
                                {t('controls.sort-options.grade-asc', 'Grade (Ascending)')}
                            </MenuItem>
                            <MenuItem value={'grade-desc'}>
                                {t('controls.sort-options.grade-desc', 'Grade (Descending)')}
                            </MenuItem>
                        </Select>
                    </Stack>
                </Stack>
                <Divider sx={{ my: 2 }} />
                {options.map(
                    ({ key, value, number, isGraded, suggestedGrade, actualGrade, maxPossibleGrade }, index) => {
                        return (
                            <CustomMenuItem
                                key={`${key}-${index}`}
                                id={key}
                                onClick={(e) => handleClose(e, { key, value })}
                            >
                                <Stack sx={{ width: '100%' }} alignItems={'center'} direction={'row'} spacing={1}>
                                    <Tooltip
                                        title={
                                            isGraded
                                                ? t('status.reviewed', 'Reviewed')
                                                : t('status.ready-for-review', 'Ready for Review')
                                        }
                                        placement={'left'}
                                    >
                                        <CustomStack alignItems={'center'} direction={'row'} spacing={1}>
                                            <Text color={'inherit'}>{number}</Text>
                                            <Icon kind={isGraded ? 'checkmark' : 'clock'} />
                                        </CustomStack>
                                    </Tooltip>
                                    <Text sx={{ flex: 1 }} noWrap>
                                        {value}
                                    </Text>
                                    <Tooltip
                                        title={
                                            actualGrade !== null
                                                ? t('ui.confirmed-grade', 'Confirmed Grade')
                                                : t('ui.suggested-grade', 'Suggested Grade')
                                        }
                                        placement={'right'}
                                    >
                                        <Box>
                                            <Text>
                                                <Text
                                                    color={actualGrade !== null ? 'inherit' : 'primary'}
                                                    component={'span'}
                                                >
                                                    {actualGrade ?? suggestedGrade}
                                                </Text>
                                                /{maxPossibleGrade}
                                            </Text>
                                        </Box>
                                    </Tooltip>
                                </Stack>
                            </CustomMenuItem>
                        );
                    }
                )}
            </CustomMenu>
        </Box>
    );
};

const CustomMenu = styled(Menu)(({ theme }) => {
    return {
        '& .MuiBackdrop-root': {
            background: 'transparent',
        },
        '& .MuiPaper-root': {
            borderRadius: '8px',
            width: '325px',
            boxShadow: theme.palette.ensightful.shadows[1],
        },
        '& .MuiList-root': {
            padding: theme.spacing(2),
        },
    };
});

const CustomMenuItem = styled(MenuItem)(({ theme }) => {
    return {
        '&:hover .MuiBox-root': {
            background: theme.palette.ensightful.shades[0],
        },
        '& .MuiBox-root': {
            borderRadius: '4px',
            background: theme.palette.ensightful.primary[50],
            padding: theme.spacing(1),
        },
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '50px',
        marginRight: theme.spacing(1),
        padding: theme.spacing(1, 2),
        background: theme.palette.ensightful.neutral[700],
        color: theme.palette.ensightful.shades[0],
    };
});

export default SubmissionNavigationDropdown;
